import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  type SupportedCurrency,
  type FxRates,
  BASE_CURRENCY,
  getDisplayCurrency,
  hasManualCurrencyOverride,
  setDisplayCurrency as setDisplayCurrencyStorage,
  clearManualCurrencyOverride,
  currencyForCountry,
  formatMoney as formatMoneyUtil,
  formatAmount as formatAmountUtil,
  formatAmountIn as formatAmountInUtil,
  convertFromMAD as convertFromMADUtil,
  convertToMAD as convertToMADUtil,
  convertCurrency as convertCurrencyUtil,
  formatMAD,
  FALLBACK_RATES,
} from "@/lib/money";

interface CurrencyContextType {
  /** Current display currency (auto-defaulted from pickup country, user-overridable) */
  displayCurrency: SupportedCurrency;
  /** User explicitly picked this currency — pickup-country defaults must not override */
  isManualOverride: boolean;
  /** Explicit user selection (persisted, wins over auto defaults) */
  setDisplayCurrency: (c: SupportedCurrency) => void;
  /** Apply the pickup-country default. No-op when the user has overridden. */
  applyCountryDefault: (iso2: string | null | undefined) => void;
  /** Drop the manual override and fall back to auto defaults */
  resetToAutoCurrency: () => void;
  /** Loaded FX rates (1 MAD = X) */
  fxRates: FxRates;
  ratesLoaded: boolean;
  /** Format a MAD amount in the display currency */
  formatMoney: (amountMad: number, opts?: { showSymbol?: boolean; decimals?: number }) => string;
  /** Format an amount already expressed in `currency` — no conversion */
  formatAmount: (amount: number, currency: string, opts?: { showSymbol?: boolean; decimals?: number }) => string;
  /** Convert from `sourceCurrency` into the display currency, then format */
  formatIn: (amount: number, sourceCurrency: string, opts?: { showSymbol?: boolean; decimals?: number }) => string;
  /** Format explicitly as MAD */
  formatMAD: (amountMad: number) => string;
  convertFromMAD: (amountMad: number) => number;
  convertToMAD: (amount: number, fromCurrency?: string) => number;
  /** Convert between any two currencies via the MAD base */
  convert: (amount: number, from: string, to?: string) => number;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const CurrencyProvider = ({ children }: { children: ReactNode }) => {
  const [displayCurrency, setDisplayCurrencyState] = useState<SupportedCurrency>(getDisplayCurrency());
  const [isManualOverride, setIsManualOverride] = useState<boolean>(hasManualCurrencyOverride());
  const [fxRates, setFxRates] = useState<FxRates>(FALLBACK_RATES);
  const [ratesLoaded, setRatesLoaded] = useState(false);
  const manualRef = useRef(isManualOverride);
  manualRef.current = isManualOverride;

  // Load FX rates from the single rate table (currency_rates)
  useEffect(() => {
    const loadRates = async () => {
      try {
        const { data, error } = await supabase
          .from('currency_rates')
          .select('currency_code, rate_to_mad');

        if (error || !data) {
          console.warn('Failed to load FX rates, using fallbacks:', error);
          return;
        }

        // rate_to_mad = MAD per 1 unit of currency → our format is 1 MAD = X currency
        const rates: FxRates = { MAD: 1 };
        data.forEach((row) => {
          if (row.rate_to_mad > 0) rates[row.currency_code] = 1 / row.rate_to_mad;
        });
        setFxRates(rates);
        setRatesLoaded(true);
      } catch (err) {
        console.warn('Error loading FX rates:', err);
      }
    };

    loadRates();
    const interval = setInterval(loadRates, 6 * 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Cross-tab / cross-component sync
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e: Event) => setDisplayCurrencyState((e as CustomEvent).detail as SupportedCurrency);
    window.addEventListener('currency-changed', handler);
    return () => window.removeEventListener('currency-changed', handler);
  }, []);

  const setDisplayCurrency = useCallback((c: SupportedCurrency) => {
    setDisplayCurrencyState(c);
    setIsManualOverride(true);
    setDisplayCurrencyStorage(c, { manual: true });
  }, []);

  const applyCountryDefault = useCallback((iso2: string | null | undefined) => {
    if (manualRef.current) return;
    const next = currencyForCountry(iso2);
    if (!next) return;
    setDisplayCurrencyState((prev) => {
      if (prev === next) return prev;
      setDisplayCurrencyStorage(next);
      return next;
    });
  }, []);

  const resetToAutoCurrency = useCallback(() => {
    clearManualCurrencyOverride();
    setIsManualOverride(false);
  }, []);

  const formatMoney = useCallback(
    (amountMad: number, opts?: { showSymbol?: boolean; decimals?: number }) =>
      formatMoneyUtil(amountMad, displayCurrency, fxRates, opts),
    [displayCurrency, fxRates]
  );

  const formatAmount = useCallback(
    (amount: number, currency: string, opts?: { showSymbol?: boolean; decimals?: number }) =>
      formatAmountUtil(amount, currency, opts),
    []
  );

  const formatIn = useCallback(
    (amount: number, sourceCurrency: string, opts?: { showSymbol?: boolean; decimals?: number }) =>
      formatAmountInUtil(amount, sourceCurrency, displayCurrency, fxRates, opts),
    [displayCurrency, fxRates]
  );

  const convertFromMAD = useCallback(
    (amountMad: number) => convertFromMADUtil(amountMad, displayCurrency, fxRates),
    [displayCurrency, fxRates]
  );

  const convertToMAD = useCallback(
    (amount: number, fromCurrency?: string) =>
      convertToMADUtil(amount, fromCurrency ?? displayCurrency, fxRates),
    [displayCurrency, fxRates]
  );

  const convert = useCallback(
    (amount: number, from: string, to?: string) =>
      convertCurrencyUtil(amount, from, to ?? displayCurrency, fxRates),
    [displayCurrency, fxRates]
  );

  return (
    <CurrencyContext.Provider
      value={{
        displayCurrency,
        isManualOverride,
        setDisplayCurrency,
        applyCountryDefault,
        resetToAutoCurrency,
        fxRates,
        ratesLoaded,
        formatMoney,
        formatAmount,
        formatIn,
        formatMAD,
        convertFromMAD,
        convertToMAD,
        convert,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within CurrencyProvider");
  return ctx;
};

export { BASE_CURRENCY };
